import type { InputManager } from '../input/InputManager';
import type { WorldManager } from '../world/WorldManager';
import {
  PLAYER_MAX_HEALTH,
  type EntityState,
  type GameConfig,
  type Vector3D,
  copyVec3,
  setVec3,
  vec3,
} from '../types';

export interface PlayerInputState {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  /** Analog forward axis (-1..1), from keyboard or left stick. */
  moveForwardAxis: number;
  /** Analog strafe axis (-1..1, positive = right). */
  moveStrafeAxis: number;
  /** Yaw (radians) around world Y — from mouse / right stick. */
  yaw: number;
  /** Pitch (radians) around local X — clamped look. */
  pitch: number;
}

export interface FallImpactResult {
  died: boolean;
  impactSpeed: number;
}

const EPSILON = 1e-6;

/**
 * Lightweight AABB player controller.
 * Runs exclusively on the fixed timestep. Sweeping collision resolves X → Y → Z
 * against solid voxels from WorldManager. Jump is grounded-only.
 */
export class PlayerController {
  readonly state: EntityState;
  readonly previousPosition: Vector3D;
  readonly input: PlayerInputState;

  private readonly config: GameConfig;
  private readonly world: WorldManager;
  private readonly inputManager: InputManager;
  private readonly wishDir = vec3();
  private jumpBuffered = false;

  constructor(
    playerId: string,
    world: WorldManager,
    config: GameConfig,
    spawn: Vector3D,
    inputManager: InputManager,
  ) {
    this.world = world;
    this.config = config;
    this.inputManager = inputManager;

    this.state = {
      id: playerId,
      position: { ...spawn },
      velocity: vec3(),
      halfExtents: { ...config.playerHalfExtents },
      onGround: false,
      alive: true,
      health: PLAYER_MAX_HEALTH,
      maxHealth: PLAYER_MAX_HEALTH,
    };
    this.previousPosition = { ...spawn };
    this.input = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      jump: false,
      moveForwardAxis: 0,
      moveStrafeAxis: 0,
      yaw: 0,
      pitch: 0,
    };
  }

  dispose(): void {
    // DOM / gamepad listeners live on InputManager.
  }

  /** Snapshot position before integrating — used by the renderer for interpolation. */
  beginTick(): void {
    copyVec3(this.previousPosition, this.state.position);
  }

  /**
   * Fixed-timestep integration. Returns fall-impact info when landing so the
   * Engine can emit death events without coupling physics to game rules.
   */
  fixedUpdate(dt: number): FallImpactResult {
    const result: FallImpactResult = { died: false, impactSpeed: 0 };
    if (!this.state.alive) return result;

    this.syncInputFlags();
    this.applyJump();
    this.applyMovementForces(dt);
    this.applyGravity(dt);

    const wasOnGround = this.state.onGround;
    const velocityBeforeResolve = this.state.velocity.y;

    this.integrateAndCollide(dt);

    // Fall damage: only when transitioning airborne → grounded with high impact.
    if (!wasOnGround && this.state.onGround && velocityBeforeResolve < 0) {
      const impactSpeed = -velocityBeforeResolve;
      result.impactSpeed = impactSpeed;
      if (impactSpeed >= this.config.fallDamageThreshold) {
        result.died = true;
        this.state.alive = false;
        this.state.velocity.x = 0;
        this.state.velocity.y = 0;
        this.state.velocity.z = 0;
      }
    }

    return result;
  }

  /** Linearly interpolate between previous and current sim positions for rendering. */
  getRenderPosition(alpha: number, out: Vector3D): Vector3D {
    const a = this.previousPosition;
    const b = this.state.position;
    out.x = a.x + (b.x - a.x) * alpha;
    out.y = a.y + (b.y - a.y) * alpha;
    out.z = a.z + (b.z - a.z) * alpha;
    return out;
  }

  respawn(spawn: Vector3D): void {
    copyVec3(this.state.position, spawn);
    copyVec3(this.previousPosition, spawn);
    setVec3(this.state.velocity, 0, 0, 0);
    this.state.onGround = false;
    this.state.alive = true;
    this.state.health = this.state.maxHealth;
  }

  /**
   * Apply combat / hazard damage. Returns true if this hit killed the player.
   */
  takeDamage(amount: number): boolean {
    if (!this.state.alive || amount <= 0) return false;
    this.state.health = Math.max(0, this.state.health - amount);
    if (this.state.health <= 0) {
      this.state.alive = false;
      this.state.velocity.x = 0;
      this.state.velocity.y = 0;
      this.state.velocity.z = 0;
      return true;
    }
    return false;
  }

  /** Shove the player away from a hit source (combat juice). */
  applyKnockback(dirX: number, dirZ: number, strength = 9.5): void {
    if (!this.state.alive) return;
    const len = Math.hypot(dirX, dirZ);
    if (len < 1e-6) return;
    const inv = 1 / len;
    this.state.velocity.x = dirX * inv * strength;
    this.state.velocity.z = dirZ * inv * strength;
    this.state.velocity.y = Math.max(this.state.velocity.y, 3.4);
    this.state.onGround = false;
  }

  // ---------------------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------------------

  private syncInputFlags(): void {
    const axes = this.inputManager.getMovementAxes();
    this.input.moveForwardAxis = axes.forward;
    this.input.moveStrafeAxis = axes.strafe;
    this.input.forward = axes.forward > 0.2;
    this.input.backward = axes.forward < -0.2;
    this.input.left = axes.strafe < -0.2;
    this.input.right = axes.strafe > 0.2;
    this.input.yaw = this.inputManager.getYaw();
    this.input.pitch = this.inputManager.getPitch();

    // Edge-triggered jump from InputManager; buffer until grounded.
    if (this.inputManager.consumePressed('jump')) {
      this.jumpBuffered = true;
    }
    this.input.jump = this.jumpBuffered;
  }

  // ---------------------------------------------------------------------------
  // Forces
  // ---------------------------------------------------------------------------

  private applyJump(): void {
    if (!this.jumpBuffered) return;
    if (!this.state.onGround) return;
    this.state.velocity.y = this.config.jumpVelocity;
    this.state.onGround = false;
    this.jumpBuffered = false;
  }

  private applyMovementForces(dt: number): void {
    const { input, wishDir, state, config } = this;
    const sinY = Math.sin(input.yaw);
    const cosY = Math.cos(input.yaw);

    // Camera-relative planar wish direction from analog axes (keyboard = ±1).
    const forward = clampAxis(input.moveForwardAxis);
    const strafe = clampAxis(input.moveStrafeAxis);
    let wx = -sinY * forward + cosY * strafe;
    let wz = -cosY * forward - sinY * strafe;

    const lenSq = wx * wx + wz * wz;
    const stickMag = Math.min(1, Math.hypot(forward, strafe));
    if (lenSq > EPSILON) {
      const inv = 1 / Math.sqrt(lenSq);
      wishDir.x = wx * inv * stickMag;
      wishDir.z = wz * inv * stickMag;
    } else {
      wishDir.x = 0;
      wishDir.z = 0;
    }

    const control = state.onGround ? 1 : config.airControlMultiplier;
    const accel = config.moveAcceleration * control;

    state.velocity.x += wishDir.x * accel * dt;
    state.velocity.z += wishDir.z * accel * dt;

    // Clamp horizontal speed.
    const hx = state.velocity.x;
    const hz = state.velocity.z;
    const hSpeed = Math.hypot(hx, hz);
    if (hSpeed > config.maxHorizontalSpeed) {
      const scale = config.maxHorizontalSpeed / hSpeed;
      state.velocity.x = hx * scale;
      state.velocity.z = hz * scale;
    }

    // Friction — only damp horizontal axes.
    const friction = state.onGround ? config.groundFriction : config.airFriction;
    const damp = Math.max(0, 1 - friction * dt);
    if (wishDir.x === 0 && wishDir.z === 0) {
      state.velocity.x *= damp;
      state.velocity.z *= damp;
    } else {
      // Light drag even while accelerating so we don't feel ice-skating.
      state.velocity.x *= Math.max(0, 1 - friction * 0.15 * dt);
      state.velocity.z *= Math.max(0, 1 - friction * 0.15 * dt);
    }
  }

  private applyGravity(dt: number): void {
    const { state, config } = this;
    state.velocity.y += config.gravity * dt;
    if (state.velocity.y < config.terminalVelocity) {
      state.velocity.y = config.terminalVelocity;
    }
  }

  // ---------------------------------------------------------------------------
  // Sweeping AABB vs voxel grid
  // ---------------------------------------------------------------------------

  private integrateAndCollide(dt: number): void {
    const { state } = this;
    const dx = state.velocity.x * dt;
    const dy = state.velocity.y * dt;
    const dz = state.velocity.z * dt;

    state.onGround = false;

    // Resolve axes independently (sweep). Order: X, Z, then Y so floor
    // contact is authoritative after lateral slides.
    if (dx !== 0) this.moveAxis('x', dx);
    if (dz !== 0) this.moveAxis('z', dz);
    if (dy !== 0) this.moveAxis('y', dy);
    else {
      // Still probe downward slightly to keep onGround sticky on flat floors.
      this.probeGround();
    }
  }

  private moveAxis(axis: 'x' | 'y' | 'z', delta: number): void {
    const { state, world, config } = this;
    const pos = state.position;
    const he = state.halfExtents;
    const bs = config.blockSize;

    pos[axis] += delta;

    const minX = pos.x - he.x;
    const maxX = pos.x + he.x;
    const minY = pos.y - he.y;
    const maxY = pos.y + he.y;
    const minZ = pos.z - he.z;
    const maxZ = pos.z + he.z;

    const vx0 = Math.floor(minX / bs);
    const vx1 = Math.floor((maxX - EPSILON) / bs);
    const vy0 = Math.floor(minY / bs);
    const vy1 = Math.floor((maxY - EPSILON) / bs);
    const vz0 = Math.floor(minZ / bs);
    const vz1 = Math.floor((maxZ - EPSILON) / bs);

    const sign = delta > 0 ? 1 : -1;

    for (let y = vy0; y <= vy1; y++) {
      for (let z = vz0; z <= vz1; z++) {
        for (let x = vx0; x <= vx1; x++) {
          if (!world.isSolid(x, y, z)) continue;

          const bMinX = x * bs;
          const bMaxX = bMinX + bs;
          const bMinY = y * bs;
          const bMaxY = bMinY + bs;
          const bMinZ = z * bs;
          const bMaxZ = bMinZ + bs;

          // Broad-phase overlap on the other two axes is implied by the loop
          // bounds; still verify full AABB intersection after the move.
          if (
            maxX <= bMinX ||
            minX >= bMaxX ||
            maxY <= bMinY ||
            minY >= bMaxY ||
            maxZ <= bMinZ ||
            minZ >= bMaxZ
          ) {
            continue;
          }

          if (axis === 'x') {
            if (sign > 0) pos.x = bMinX - he.x - EPSILON;
            else pos.x = bMaxX + he.x + EPSILON;
            state.velocity.x = 0;
          } else if (axis === 'z') {
            if (sign > 0) pos.z = bMinZ - he.z - EPSILON;
            else pos.z = bMaxZ + he.z + EPSILON;
            state.velocity.z = 0;
          } else {
            if (sign > 0) {
              // Hitting a ceiling.
              pos.y = bMinY - he.y - EPSILON;
              state.velocity.y = 0;
            } else {
              // Hitting a floor.
              pos.y = bMaxY + he.y + EPSILON;
              state.velocity.y = 0;
              state.onGround = true;
            }
          }
        }
      }
    }

    // World bounds clamp — treat out-of-world as solid stone walls.
    this.clampToWorldBounds(axis);
  }

  private clampToWorldBounds(axis: 'x' | 'y' | 'z'): void {
    const { state, world, config } = this;
    const he = state.halfExtents;
    const bs = config.blockSize;
    const maxX = world.width * bs;
    const maxY = world.height * bs;
    const maxZ = world.depth * bs;

    if (axis === 'x') {
      if (state.position.x - he.x < 0) {
        state.position.x = he.x + EPSILON;
        state.velocity.x = 0;
      } else if (state.position.x + he.x > maxX) {
        state.position.x = maxX - he.x - EPSILON;
        state.velocity.x = 0;
      }
    } else if (axis === 'z') {
      if (state.position.z - he.z < 0) {
        state.position.z = he.z + EPSILON;
        state.velocity.z = 0;
      } else if (state.position.z + he.z > maxZ) {
        state.position.z = maxZ - he.z - EPSILON;
        state.velocity.z = 0;
      }
    } else {
      if (state.position.y - he.y < 0) {
        state.position.y = he.y + EPSILON;
        state.velocity.y = 0;
        state.onGround = true;
      } else if (state.position.y + he.y > maxY) {
        state.position.y = maxY - he.y - EPSILON;
        state.velocity.y = 0;
      }
    }
  }

  private probeGround(): void {
    const { state, world, config } = this;
    const he = state.halfExtents;
    const bs = config.blockSize;
    const probe = 0.08;

    const minX = state.position.x - he.x + EPSILON;
    const maxX = state.position.x + he.x - EPSILON;
    const minZ = state.position.z - he.z + EPSILON;
    const maxZ = state.position.z + he.z - EPSILON;
    const feetY = state.position.y - he.y;
    const probeY = feetY - probe;

    const vx0 = Math.floor(minX / bs);
    const vx1 = Math.floor(maxX / bs);
    const vz0 = Math.floor(minZ / bs);
    const vz1 = Math.floor(maxZ / bs);
    const vy = Math.floor(probeY / bs);

    for (let z = vz0; z <= vz1; z++) {
      for (let x = vx0; x <= vx1; x++) {
        if (world.isSolid(x, vy, z)) {
          state.onGround = true;
          return;
        }
      }
    }
  }
}

function clampAxis(v: number): number {
  if (v > 1) return 1;
  if (v < -1) return -1;
  return v;
}
